import { useCallback } from 'react';
import {
  GenericError,
  InternalServerError,
  MissingParameterError,
  NotFoundError,
  UnauthorizedError,
} from 'src/api/common/error/HttpError';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ApiEnvelope } from 'src/api/common/api/ApiEnvelope';

import { useTranslation } from 'react-i18next';
import { useWfErrorStore } from '../../stores/WfErrorStore';
import wfAxiosInstance from './wfAxiosInstance';

function useWfApi() {
  const { addError, clearErrors } = useWfErrorStore();
  const { t } = useTranslation();

  const requestWrapper = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async <T, U = any>(
      method: 'get' | 'post' | 'patch' | 'delete',
      url: string,
      bodyOrConfig?: U | AxiosRequestConfig,
      config?: AxiosRequestConfig
    ): Promise<T | null> => {
      clearErrors();
      try {
        let response: AxiosResponse<ApiEnvelope<T>>;
        if (method === 'get') {
          response = await wfAxiosInstance.get<ApiEnvelope<T>>(
            url,
            bodyOrConfig as AxiosRequestConfig
          );
        } else if (method === 'post') {
          response = await wfAxiosInstance.post<ApiEnvelope<T>>(url, bodyOrConfig, config);
        } else if (method === 'patch') {
          response = await wfAxiosInstance.patch<ApiEnvelope<T>>(url, bodyOrConfig, config);
        } else {
          response = await wfAxiosInstance.delete<ApiEnvelope<T>>(
            url,
            bodyOrConfig as AxiosRequestConfig
          );
        }

        const { status, data, request } = response;

        if (status === 200 && data.success) {
          const payload = (data.result ?? null) as T | null;
          if (payload) return payload;

          addError(new MissingParameterError(status, data.errorCodes ?? [], request));
          return null;
        }

        if (status === 200 && !data.success) {
          addError(new MissingParameterError(status, data.errorCodes ?? [], request));
          return null;
        }

        addError(new GenericError(status, data.errorCodes ?? [], request));
        return null;
      } catch (error) {
        if (axios.isAxiosError(error)) {
          if (error.code === 'ECONNABORTED') {
            throw error;
          }
          if (error.response) {
            handleCatchError(error as AxiosError<ApiEnvelope<unknown>>);
          } else {
            addError(new GenericError(-1, [], ''));
          }
        } else {
          addError(new GenericError(-1, [], ''));
        }
        return null;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const get = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async <T, U = any>(url: string, body?: U, config?: AxiosRequestConfig) =>
      requestWrapper<T, U>('get', url, body, config),
    [requestWrapper]
  );

  const post = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async <T, U = any>(url: string, body?: U, config?: AxiosRequestConfig) =>
      requestWrapper<T, U>('post', url, body, config),
    [requestWrapper]
  );

  const patch = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async <T, U = any>(url: string, body?: U, config?: AxiosRequestConfig) =>
      requestWrapper<T, U>('patch', url, body, config),
    [requestWrapper]
  );

  const del = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async <T, U = any>(url: string, config?: AxiosRequestConfig) =>
      requestWrapper<T, U>('delete', url, config),
    [requestWrapper]
  );

  const postVoid = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async <U = any>(url: string, body?: U, config?: AxiosRequestConfig): Promise<boolean> => {
      clearErrors();
      try {
        const response = await wfAxiosInstance.post<ApiEnvelope<void>>(url, body, config);
        const { status, data, request } = response;

        if (status === 200 && data.success) {
          return true;
        }

        addError(new MissingParameterError(status, data.errorCodes ?? [], request));
        return false;
      } catch (error) {
        if (axios.isAxiosError(error)) {
          handleCatchError(error as AxiosError<ApiEnvelope<unknown>>);
        }
        addError(new GenericError(-1, [], ''));
        return false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  function handleCatchError(axiosError: AxiosError<ApiEnvelope<unknown>, unknown>) {
    const { config: errorConfig, request: rawRequest } = axiosError;
    const { status } = axiosError.response as AxiosResponse;
    const responseUrl =
      (rawRequest as { responseURL?: string } | undefined)?.responseURL ??
      axios.getUri(errorConfig);

    switch (status) {
      case 401:
        addError(
          new UnauthorizedError(status, resolveErrorMessage(axiosError.response?.data), responseUrl)
        );
        break;
      case 404:
        addError(
          new NotFoundError(status, resolveErrorMessage(axiosError.response?.data), responseUrl)
        );
        break;
      case 500:
        addError(
          new InternalServerError(
            status,
            resolveErrorMessage(axiosError.response?.data),
            responseUrl
          )
        );
        break;
      default:
        addError(new GenericError(-1, [], ''));
        break;
    }
  }

  function resolveErrorMessage(data: ApiEnvelope<unknown> | undefined): string[] {
    if (data === undefined) return ['no data provided'];
    if (data.errorCodes?.length) return data.errorCodes.map((code) => t(`eris.httpError,${code}`));
    return [];
  }

  return { get, post, patch, del, postVoid };
}

export default useWfApi;
